// ──── Interfaces ────

export interface FormFieldDef {
    key: string;
    labelKey: string;
    defaultLabel: Record<string, string>;
    defaultPlaceholder: Record<string, string>;
    defaultRequired: boolean;
    type: "text" | "dropdown" | "date" | "time" | "textarea" | "yesno" | "file";
}

export interface SectionDef {
    key: string;
    titleKey: string;
    defaultTitle: Record<string, string>;
    fields: FormFieldDef[];
}

export interface InfoBlockConfig {
    BackgroundColor: string;
    Image: {
        Desktop: MediaDef;
        Mobile: MediaDef;
    };
    Title: Record<string, string>;
    TitleVisible: boolean;
    Description: Record<string, string>;
    DescriptionVisible: boolean;
    Advantages: { Text: Record<string, string>; Activer: boolean }[];
}

export interface SubmitButtonConfig {
    Label: Record<string, string>;
    Color: string;
}

export interface LegalTextConfig {
    Content: Record<string, string>;
    Visible: boolean;
}

export interface FormConfig {
    basePath: string[];
    defaultInfoBlock: InfoBlockConfig;
    defaultFormTitle: Record<string, string>;
    defaultFormSubtitle: Record<string, string>;
    defaultSubmitButton: SubmitButtonConfig;
    defaultLegalText: LegalTextConfig;
    sectionDefs: SectionDef[];
}

interface MediaDef {
    Url: string;
    Name: string;
    Type: string;
    Extenxion: string;
}

// ──── Shared constants ────

export const DEFAULT_EMPTY_MEDIA: MediaDef = {
    Url: "",
    Name: "",
    Type: "",
    Extenxion: "",
};

export const DEFAULT_LANGS: Record<string, string> = {
    FR: "",
    EN: "",
    ES: "",
    IT: "",
    AL: "",
};

export const SWITCH_PROPS = {
    offColor: "#faafb4",
    offHandleColor: "#E30613",
    onColor: "#c2eec4",
    onHandleColor: "#34C38F",
    height: 19,
    handleDiameter: 24,
    uncheckedIcon: false as const,
    checkedIcon: false as const,
};

export const SWITCH_PROPS_SMALL = {
    ...SWITCH_PROPS,
    height: 16,
    handleDiameter: 20,
};

export const DEFAULT_FORM_SUBTITLE: Record<string, string> = {
    FR: "Tous les champs marqués * sont obligatoires",
    EN: "All fields marked * are required",
    ES: "Todos los campos marcados con * son obligatorios",
    IT: "Tutti i campi contrassegnati con * sono obbligatori",
    AL: "Alle mit * markierten Felder sind Pflichtfelder",
};

export const DEFAULT_LEGAL_TEXT: LegalTextConfig = {
    Content: {
        FR:
            "J'accepte la politique de données pour le traitement de ma demande.",
        EN: "I accept the data policy for processing my request.",
        ES: "Acepto la política de datos para el tratamiento de mi solicitud.",
        IT:
            "Accetto la politica dei dati per il trattamento della mia richiesta.",
        AL:
            "Ich akzeptiere die Datenschutzrichtlinie zur Bearbeitung meiner Anfrage.",
    },
    Visible: true,
};

export const TYPE_LABELS: Record<string, string> = {
    text: "Texte",
    dropdown: "Liste",
    date: "Date",
    time: "Heure",
    textarea: "Zone texte",
    yesno: "Oui/Non",
    file: "Fichier",
};

// ══════════════════════════════════════════
//  BECOME A FRANCHISEE
// ══════════════════════════════════════════

const FRANCHISEE_IDENTITY_FIELDS: FormFieldDef[] = [
    {
        key: "FirstName",
        labelKey: "First name",
        defaultLabel: {
            FR: "Prénom",
            EN: "First name",
            ES: "Nombre",
            IT: "Nome",
            AL: "Vorname",
        },
        defaultPlaceholder: {
            FR: "Jean",
            EN: "John",
            ES: "Juan",
            IT: "Giovanni",
            AL: "Johann",
        },
        defaultRequired: true,
        type: "text",
    },
    {
        key: "LastName",
        labelKey: "Last name",
        defaultLabel: {
            FR: "Nom",
            EN: "Last name",
            ES: "Apellido",
            IT: "Cognome",
            AL: "Nachname",
        },
        defaultPlaceholder: {
            FR: "Dupont",
            EN: "Smith",
            ES: "García",
            IT: "Rossi",
            AL: "Müller",
        },
        defaultRequired: true,
        type: "text",
    },
    {
        key: "Email",
        labelKey: "Email",
        defaultLabel: {
            FR: "Email",
            EN: "Email",
            ES: "Email",
            IT: "Email",
            AL: "E-Mail",
        },
        defaultPlaceholder: {
            FR: "jean.dupont@email.com",
            EN: "john.smith@email.com",
            ES: "juan.garcia@email.com",
            IT: "giovanni.rossi@email.com",
            AL: "johann.mueller@email.com",
        },
        defaultRequired: true,
        type: "text",
    },
    {
        key: "Phone",
        labelKey: "Phone",
        defaultLabel: {
            FR: "Téléphone",
            EN: "Phone",
            ES: "Teléfono",
            IT: "Telefono",
            AL: "Telefon",
        },
        defaultPlaceholder: {
            FR: "06 12 34 56 78",
            EN: "06 12 34 56 78",
            ES: "06 12 34 56 78",
            IT: "06 12 34 56 78",
            AL: "06 12 34 56 78",
        },
        defaultRequired: true,
        type: "text",
    },
];

const FRANCHISEE_PROFESSIONAL_FIELDS: FormFieldDef[] = [
    {
        key: "Profession",
        labelKey: "Profession",
        defaultLabel: {
            FR: "Profession",
            EN: "Profession",
            ES: "Profesión",
            IT: "Professione",
            AL: "Beruf",
        },
        defaultPlaceholder: {
            FR: "Sélectionnez votre profession",
            EN: "Select your profession",
            ES: "Seleccione su profesión",
            IT: "Seleziona la tua professione",
            AL: "Wählen Sie Ihren Beruf",
        },
        defaultRequired: false,
        type: "dropdown",
    },
    {
        key: "PersonalContribution",
        labelKey: "Personal contribution (€)",
        defaultLabel: {
            FR: "Apport personnel (€)",
            EN: "Personal contribution (€)",
            ES: "Aportación personal (€)",
            IT: "Contributo personale (€)",
            AL: "Eigenkapital (€)",
        },
        defaultPlaceholder: {
            FR: "Ex : 50 000",
            EN: "Ex: 50,000",
            ES: "Ej: 50.000",
            IT: "Es: 50.000",
            AL: "Bsp: 50.000",
        },
        defaultRequired: true,
        type: "text",
    },
];

const FRANCHISEE_PROJECT_FIELDS: FormFieldDef[] = [
    {
        key: "CommercialPremises",
        labelKey: "Commercial premises available?",
        defaultLabel: {
            FR: "Local commercial disponible ?",
            EN: "Commercial premises available?",
            ES: "¿Local comercial disponible?",
            IT: "Locale commerciale disponibile?",
            AL: "Geschäftsräume verfügbar?",
        },
        defaultPlaceholder: { ...DEFAULT_LANGS },
        defaultRequired: false,
        type: "yesno",
    },
    {
        key: "MultiFranchisee",
        labelKey: "Already multi-franchisee?",
        defaultLabel: {
            FR: "Déjà multi-franchisé ?",
            EN: "Already multi-franchisee?",
            ES: "¿Ya multi-franquiciado?",
            IT: "Già multi-franchisee?",
            AL: "Bereits Multi-Franchisenehmer?",
        },
        defaultPlaceholder: { ...DEFAULT_LANGS },
        defaultRequired: false,
        type: "yesno",
    },
    {
        key: "WhichFranchise",
        labelKey: "If yes, which franchise?",
        defaultLabel: {
            FR: "Si oui, quelle franchise ?",
            EN: "If yes, which franchise?",
            ES: "Si es así, ¿qué franquicia?",
            IT: "Se sì, quale franchising?",
            AL: "Wenn ja, welches Franchise?",
        },
        defaultPlaceholder: {
            FR: "Nom de la franchise",
            EN: "Franchise name",
            ES: "Nombre de la franquicia",
            IT: "Nome del franchising",
            AL: "Name des Franchise",
        },
        defaultRequired: false,
        type: "text",
    },
    {
        key: "TargetCity",
        labelKey: "Target city / department",
        defaultLabel: {
            FR: "Ville / Département ciblé",
            EN: "Target city / department",
            ES: "Ciudad / Departamento objetivo",
            IT: "Città / Dipartimento target",
            AL: "Zielstadt / Abteilung",
        },
        defaultPlaceholder: {
            FR: "Ex : Lyon, Paris 11e...",
            EN: "Ex: Lyon, Paris 11th...",
            ES: "Ej: Lyon, París 11...",
            IT: "Es: Lione, Parigi 11...",
            AL: "Bsp: Lyon, Paris 11...",
        },
        defaultRequired: true,
        type: "text",
    },
    {
        key: "Motivation",
        labelKey: "Motivation",
        defaultLabel: {
            FR: "Motivation",
            EN: "Motivation",
            ES: "Motivación",
            IT: "Motivazione",
            AL: "Motivation",
        },
        defaultPlaceholder: {
            FR:
                "Dites-nous en quelques lignes pourquoi vous souhaitez rejoindre notre réseau...",
            EN: "Tell us in a few lines why you want to join our network...",
            ES:
                "Cuéntenos en pocas líneas por qué desea unirse a nuestra red...",
            IT:
                "Raccontaci in poche righe perché vuoi unirti alla nostra rete...",
            AL:
                "Erzählen Sie uns in wenigen Zeilen, warum Sie unserem Netzwerk beitreten möchten...",
        },
        defaultRequired: false,
        type: "textarea",
    },
];

export const FORM_KEYS = [
    {
        key: "BecomeAFranchisee",
        labelKey: "Become a Franchisee",
    },
    {
        key: "LargeOrders",
        labelKey: "Large Orders",
    },
    {
        key: "RecruitmentAds",
        labelKey: "Recruitment Ads",
    },
] as const;

export const EMAIL_CONFIG_BASE_PATH = [
    "template",
    "SiteWeb",
    "Forms",
    "EmailConfig",
] as const;

export const BECOME_FRANCHISEE_CONFIG: FormConfig = {
    basePath: ["SiteWeb", "Forms", "BecomeAFranchisee"],
    defaultInfoBlock: {
        BackgroundColor: "#8B1A1A",
        Image: {
            Desktop: { ...DEFAULT_EMPTY_MEDIA },
            Mobile: { ...DEFAULT_EMPTY_MEDIA },
        },
        Title: {
            FR: "Rejoignez l'aventure",
            EN: "Join the adventure",
            ES: "Únete a la aventura",
            IT: "Unisciti all'avventura",
            AL: "Treten Sie dem Abenteuer bei",
        },
        TitleVisible: true,
        Description: {
            FR:
                "Devenez franchisé et ouvrez votre restaurant. Faites partie d'un réseau en pleine croissance.",
            EN:
                "Become a franchisee and open your restaurant. Be part of a growing network.",
            ES:
                "Conviértase en franquiciado y abra su restaurante. Forme parte de una red en crecimiento.",
            IT:
                "Diventa franchisee e apri il tuo ristorante. Fai parte di una rete in crescita.",
            AL: "Werden Sie Franchisenehmer und eröffnen Sie Ihr Restaurant.",
        },
        DescriptionVisible: true,
        Advantages: [
            {
                Text: {
                    FR: "Accompagnement personnalisé",
                    EN: "Personalized support",
                    ES: "Acompañamiento personalizado",
                    IT: "Supporto personalizzato",
                    AL: "Persönliche Betreuung",
                },
                Activer: true,
            },
            {
                Text: {
                    FR: "Formation complète incluse",
                    EN: "Complete training included",
                    ES: "Formación completa incluida",
                    IT: "Formazione completa inclusa",
                    AL: "Vollständige Schulung inklusive",
                },
                Activer: true,
            },
            {
                Text: {
                    FR: "Concept éprouvé & rentable",
                    EN: "Proven & profitable concept",
                    ES: "Concepto probado y rentable",
                    IT: "Concetto collaudato e redditizio",
                    AL: "Bewährtes & profitables Konzept",
                },
                Activer: true,
            },
            {
                Text: {
                    FR: "+30% de croissance / an",
                    EN: "+30% growth / year",
                    ES: "+30% de crecimiento / año",
                    IT: "+30% di crescita / anno",
                    AL: "+30% Wachstum / Jahr",
                },
                Activer: true,
            },
            {
                Text: {
                    FR: "56 restaurants en France",
                    EN: "56 restaurants in France",
                    ES: "56 restaurantes en Francia",
                    IT: "56 ristoranti in Francia",
                    AL: "56 Restaurants in Frankreich",
                },
                Activer: true,
            },
        ],
    },
    defaultFormTitle: {
        FR: "Déposez votre candidature",
        EN: "Submit your application",
        ES: "Envíe su candidatura",
        IT: "Invia la tua candidatura",
        AL: "Reichen Sie Ihre Bewerbung ein",
    },
    defaultFormSubtitle: DEFAULT_FORM_SUBTITLE,
    defaultSubmitButton: {
        Label: {
            FR: "Envoyer ma candidature →",
            EN: "Submit my application →",
            ES: "Enviar mi candidatura →",
            IT: "Invia la mia candidatura →",
            AL: "Meine Bewerbung absenden →",
        },
        Color: "#000000",
    },
    defaultLegalText: {
        Content: {
            FR:
                "En soumettant ce formulaire, vous acceptez d'être contacté par notre équipe. Politique de confidentialité",
            EN:
                "By submitting this form, you agree to be contacted by our team. Privacy Policy",
            ES:
                "Al enviar este formulario, acepta ser contactado por nuestro equipo. Política de privacidad",
            IT:
                "Inviando questo modulo, accetti di essere contattato dal nostro team. Informativa sulla privacy",
            AL:
                "Mit dem Absenden dieses Formulars erklären Sie sich damit einverstanden, von unserem Team kontaktiert zu werden.",
        },
        Visible: true,
    },
    sectionDefs: [
        {
            key: "Identity",
            titleKey: "Identity",
            defaultTitle: {
                FR: "IDENTITÉ",
                EN: "IDENTITY",
                ES: "IDENTIDAD",
                IT: "IDENTITÀ",
                AL: "IDENTITÄT",
            },
            fields: FRANCHISEE_IDENTITY_FIELDS,
        },
        {
            key: "ProfessionalProfile",
            titleKey: "Professional profile",
            defaultTitle: {
                FR: "PROFIL PROFESSIONNEL",
                EN: "PROFESSIONAL PROFILE",
                ES: "PERFIL PROFESIONAL",
                IT: "PROFILO PROFESSIONALE",
                AL: "BERUFLICHES PROFIL",
            },
            fields: FRANCHISEE_PROFESSIONAL_FIELDS,
        },
        {
            key: "YourProject",
            titleKey: "Your project",
            defaultTitle: {
                FR: "VOTRE PROJET",
                EN: "YOUR PROJECT",
                ES: "SU PROYECTO",
                IT: "IL TUO PROGETTO",
                AL: "IHR PROJEKT",
            },
            fields: FRANCHISEE_PROJECT_FIELDS,
        },
    ],
};

// ══════════════════════════════════════════
//  LARGE ORDERS
// ══════════════════════════════════════════

const LARGE_ORDERS_FIELDS: FormFieldDef[] = [
    {
        key: "LastName",
        labelKey: "Last name",
        defaultLabel: {
            FR: "Nom",
            EN: "Last name",
            ES: "Apellido",
            IT: "Cognome",
            AL: "Nachname",
        },
        defaultPlaceholder: {
            FR: "Nom",
            EN: "Last name",
            ES: "Apellido",
            IT: "Cognome",
            AL: "Nachname",
        },
        defaultRequired: true,
        type: "text",
    },
    {
        key: "FirstName",
        labelKey: "First name",
        defaultLabel: {
            FR: "Prénom",
            EN: "First name",
            ES: "Nombre",
            IT: "Nome",
            AL: "Vorname",
        },
        defaultPlaceholder: {
            FR: "Prénom",
            EN: "First name",
            ES: "Nombre",
            IT: "Nome",
            AL: "Vorname",
        },
        defaultRequired: true,
        type: "text",
    },
    {
        key: "Company",
        labelKey: "Company",
        defaultLabel: {
            FR: "Entreprise",
            EN: "Company",
            ES: "Empresa",
            IT: "Azienda",
            AL: "Unternehmen",
        },
        defaultPlaceholder: {
            FR: "Nom de l'entreprise",
            EN: "Company name",
            ES: "Nombre de la empresa",
            IT: "Nome dell'azienda",
            AL: "Firmenname",
        },
        defaultRequired: false,
        type: "text",
    },
    {
        key: "Email",
        labelKey: "Email",
        defaultLabel: {
            FR: "E-mail",
            EN: "Email",
            ES: "E-mail",
            IT: "E-mail",
            AL: "E-Mail",
        },
        defaultPlaceholder: {
            FR: "E-mail",
            EN: "Email",
            ES: "E-mail",
            IT: "E-mail",
            AL: "E-Mail",
        },
        defaultRequired: true,
        type: "text",
    },
    {
        key: "Phone",
        labelKey: "Phone",
        defaultLabel: {
            FR: "Téléphone",
            EN: "Phone",
            ES: "Teléfono",
            IT: "Telefono",
            AL: "Telefon",
        },
        defaultPlaceholder: {
            FR: "Téléphone",
            EN: "Phone",
            ES: "Teléfono",
            IT: "Telefono",
            AL: "Telefon",
        },
        defaultRequired: true,
        type: "text",
    },
    {
        key: "DesiredQuantity",
        labelKey: "Desired pizza quantity",
        defaultLabel: {
            FR: "Quantité de pizza souhaitée",
            EN: "Desired pizza quantity",
            ES: "Cantidad de pizza deseada",
            IT: "Quantità di pizza desiderata",
            AL: "Gewünschte Pizzamenge",
        },
        defaultPlaceholder: {
            FR: "Quantité souhaitée",
            EN: "Desired quantity",
            ES: "Cantidad deseada",
            IT: "Quantità desiderata",
            AL: "Gewünschte Menge",
        },
        defaultRequired: true,
        type: "text",
    },
    {
        key: "PizzaSize",
        labelKey: "Pizza size",
        defaultLabel: {
            FR: "Taille des pizzas",
            EN: "Pizza size",
            ES: "Tamaño de las pizzas",
            IT: "Dimensione delle pizze",
            AL: "Pizzagröße",
        },
        defaultPlaceholder: {
            FR: "Sélectionner une taille",
            EN: "Select a size",
            ES: "Seleccionar un tamaño",
            IT: "Selezionare una dimensione",
            AL: "Größe auswählen",
        },
        defaultRequired: true,
        type: "dropdown",
    },
    {
        key: "DeliveryAddress",
        labelKey: "Delivery address",
        defaultLabel: {
            FR: "Adresse de livraison",
            EN: "Delivery address",
            ES: "Dirección de entrega",
            IT: "Indirizzo di consegna",
            AL: "Lieferadresse",
        },
        defaultPlaceholder: {
            FR: "Adresse de livraison",
            EN: "Delivery address",
            ES: "Dirección de entrega",
            IT: "Indirizzo di consegna",
            AL: "Lieferadresse",
        },
        defaultRequired: true,
        type: "text",
    },
    {
        key: "City",
        labelKey: "City",
        defaultLabel: {
            FR: "Ville",
            EN: "City",
            ES: "Ciudad",
            IT: "Città",
            AL: "Stadt",
        },
        defaultPlaceholder: {
            FR: "Ville",
            EN: "City",
            ES: "Ciudad",
            IT: "Città",
            AL: "Stadt",
        },
        defaultRequired: true,
        type: "text",
    },
    {
        key: "PostalCode",
        labelKey: "Postal code",
        defaultLabel: {
            FR: "Code postal",
            EN: "Postal code",
            ES: "Código postal",
            IT: "Codice postale",
            AL: "Postleitzahl",
        },
        defaultPlaceholder: {
            FR: "Code postal",
            EN: "Postal code",
            ES: "Código postal",
            IT: "Codice postale",
            AL: "Postleitzahl",
        },
        defaultRequired: true,
        type: "text",
    },
    {
        key: "DeliveryDate",
        labelKey: "Delivery date",
        defaultLabel: {
            FR: "Date de livraison",
            EN: "Delivery date",
            ES: "Fecha de entrega",
            IT: "Data di consegna",
            AL: "Lieferdatum",
        },
        defaultPlaceholder: {
            FR: "jj/mm/aaaa",
            EN: "dd/mm/yyyy",
            ES: "dd/mm/aaaa",
            IT: "gg/mm/aaaa",
            AL: "tt/mm/jjjj",
        },
        defaultRequired: true,
        type: "date",
    },
    {
        key: "DeliveryTime",
        labelKey: "Delivery time",
        defaultLabel: {
            FR: "Heure de livraison",
            EN: "Delivery time",
            ES: "Hora de entrega",
            IT: "Ora di consegna",
            AL: "Lieferzeit",
        },
        defaultPlaceholder: {
            FR: "--:--",
            EN: "--:--",
            ES: "--:--",
            IT: "--:--",
            AL: "--:--",
        },
        defaultRequired: true,
        type: "time",
    },
    {
        key: "Message",
        labelKey: "Message",
        defaultLabel: {
            FR: "Message",
            EN: "Message",
            ES: "Mensaje",
            IT: "Messaggio",
            AL: "Nachricht",
        },
        defaultPlaceholder: {
            FR: "Votre message et détails de la commande",
            EN: "Your message and order details",
            ES: "Su mensaje y detalles del pedido",
            IT: "Il tuo messaggio e i dettagli dell'ordine",
            AL: "Ihre Nachricht und Bestelldetails",
        },
        defaultRequired: false,
        type: "textarea",
    },
];

export const LARGE_ORDERS_CONFIG: FormConfig = {
    basePath: ["SiteWeb", "Forms", "LargeOrders"],
    defaultInfoBlock: {
        BackgroundColor: "#1A1A1A",
        Image: {
            Desktop: { ...DEFAULT_EMPTY_MEDIA },
            Mobile: { ...DEFAULT_EMPTY_MEDIA },
        },
        Title: {
            FR: "Commande grande quantité",
            EN: "Large quantity order",
            ES: "Pedido en grandes cantidades",
            IT: "Ordine in grandi quantità",
            AL: "Großmengenbestellung",
        },
        TitleVisible: true,
        Description: {
            FR:
                "Vous êtes une entreprise, une association, une collectivité, vous souhaitez organiser un événement, une fête d'anniversaire ?",
            EN:
                "Are you a company, association, or community looking to organize an event or birthday party?",
            ES:
                "¿Es usted una empresa, asociación o colectividad que desea organizar un evento o una fiesta de cumpleaños?",
            IT:
                "Siete un'azienda, un'associazione o una collettività che desidera organizzare un evento o una festa di compleanno?",
            AL:
                "Sind Sie ein Unternehmen, ein Verein oder eine Gemeinde und möchten eine Veranstaltung oder Geburtstagsfeier organisieren?",
        },
        DescriptionVisible: true,
        Advantages: [
            {
                Text: {
                    FR: "5% de remise pour toute commande de 50 à 99 pizzas",
                    EN: "5% discount for orders of 50 to 99 pizzas",
                    ES: "5% de descuento para pedidos de 50 a 99 pizzas",
                    IT: "5% di sconto per ordini da 50 a 99 pizze",
                    AL: "5% Rabatt für Bestellungen von 50 bis 99 Pizzen",
                },
                Activer: true,
            },
            {
                Text: {
                    FR:
                        "7,5% de remise pour toute commande de 100 à 149 pizzas",
                    EN: "7.5% discount for orders of 100 to 149 pizzas",
                    ES: "7,5% de descuento para pedidos de 100 a 149 pizzas",
                    IT: "7,5% di sconto per ordini da 100 a 149 pizze",
                    AL: "7,5% Rabatt für Bestellungen von 100 bis 149 Pizzen",
                },
                Activer: true,
            },
            {
                Text: {
                    FR: "10% de remise au-delà de 149 pizzas",
                    EN: "10% discount for orders above 149 pizzas",
                    ES: "10% de descuento para pedidos de más de 149 pizzas",
                    IT: "10% di sconto per ordini superiori a 149 pizze",
                    AL: "10% Rabatt für Bestellungen über 149 Pizzen",
                },
                Activer: true,
            },
        ],
    },
    defaultFormTitle: {
        FR: "Commande grande quantité",
        EN: "Large quantity order",
        ES: "Pedido en grandes cantidades",
        IT: "Ordine in grandi quantità",
        AL: "Großmengenbestellung",
    },
    defaultFormSubtitle: DEFAULT_FORM_SUBTITLE,
    defaultSubmitButton: {
        Label: {
            FR: "Envoyer →",
            EN: "Send →",
            ES: "Enviar →",
            IT: "Invia →",
            AL: "Senden →",
        },
        Color: "#000000",
    },
    defaultLegalText: DEFAULT_LEGAL_TEXT,
    sectionDefs: [
        {
            key: "OrderInfo",
            titleKey: "Order information",
            defaultTitle: {
                FR: "INFORMATIONS DE COMMANDE",
                EN: "ORDER INFORMATION",
                ES: "INFORMACIÓN DEL PEDIDO",
                IT: "INFORMAZIONI SULL'ORDINE",
                AL: "BESTELLINFORMATIONEN",
            },
            fields: LARGE_ORDERS_FIELDS,
        },
    ],
};

// ══════════════════════════════════════════
//  RECRUITMENT ADS
// ══════════════════════════════════════════

const RECRUITMENT_FIELDS: FormFieldDef[] = [
    {
        key: "LastName",
        labelKey: "Last name",
        defaultLabel: {
            FR: "Nom",
            EN: "Last name",
            ES: "Apellido",
            IT: "Cognome",
            AL: "Nachname",
        },
        defaultPlaceholder: {
            FR: "Nom",
            EN: "Last name",
            ES: "Apellido",
            IT: "Cognome",
            AL: "Nachname",
        },
        defaultRequired: true,
        type: "text",
    },
    {
        key: "FirstName",
        labelKey: "First name",
        defaultLabel: {
            FR: "Prénom",
            EN: "First name",
            ES: "Nombre",
            IT: "Nome",
            AL: "Vorname",
        },
        defaultPlaceholder: {
            FR: "Prénom",
            EN: "First name",
            ES: "Nombre",
            IT: "Nome",
            AL: "Vorname",
        },
        defaultRequired: true,
        type: "text",
    },
    {
        key: "Email",
        labelKey: "Email",
        defaultLabel: {
            FR: "E-mail",
            EN: "Email",
            ES: "E-mail",
            IT: "E-mail",
            AL: "E-Mail",
        },
        defaultPlaceholder: {
            FR: "E-mail",
            EN: "Email",
            ES: "E-mail",
            IT: "E-mail",
            AL: "E-Mail",
        },
        defaultRequired: true,
        type: "text",
    },
    {
        key: "Phone",
        labelKey: "Phone",
        defaultLabel: {
            FR: "Téléphone",
            EN: "Phone",
            ES: "Teléfono",
            IT: "Telefono",
            AL: "Telefon",
        },
        defaultPlaceholder: {
            FR: "Téléphone",
            EN: "Phone",
            ES: "Teléfono",
            IT: "Telefono",
            AL: "Telefon",
        },
        defaultRequired: true,
        type: "text",
    },
    {
        key: "DesiredPosition",
        labelKey: "Desired position",
        defaultLabel: {
            FR: "Poste recherché",
            EN: "Desired position",
            ES: "Puesto deseado",
            IT: "Posizione desiderata",
            AL: "Gewünschte Stelle",
        },
        defaultPlaceholder: {
            FR: "Quel(s) poste(s) recherchez-vous ?",
            EN: "What position(s) are you looking for?",
            ES: "¿Qué puesto(s) busca?",
            IT: "Quale/i posizione/i cerca?",
            AL: "Welche Stelle(n) suchen Sie?",
        },
        defaultRequired: true,
        type: "text",
    },
    {
        key: "ContractType",
        labelKey: "Contract type",
        defaultLabel: {
            FR: "Type de contrat",
            EN: "Contract type",
            ES: "Tipo de contrato",
            IT: "Tipo di contratto",
            AL: "Vertragsart",
        },
        defaultPlaceholder: {
            FR: "- Sélectionner un type de contrat -",
            EN: "- Select a contract type -",
            ES: "- Seleccionar un tipo de contrato -",
            IT: "- Selezionare un tipo di contratto -",
            AL: "- Vertragsart auswählen -",
        },
        defaultRequired: true,
        type: "dropdown",
    },
    {
        key: "Motivation",
        labelKey: "Motivation",
        defaultLabel: {
            FR: "Motivation",
            EN: "Motivation",
            ES: "Motivación",
            IT: "Motivazione",
            AL: "Motivation",
        },
        defaultPlaceholder: {
            FR: "Quelles sont vos motivations en quelques lignes",
            EN: "Describe your motivations in a few lines",
            ES: "Describa sus motivaciones en unas líneas",
            IT: "Descriva le sue motivazioni in poche righe",
            AL: "Beschreiben Sie Ihre Motivation in wenigen Zeilen",
        },
        defaultRequired: true,
        type: "textarea",
    },
    {
        key: "CV",
        labelKey: "Upload CV",
        defaultLabel: {
            FR: "Télécharger votre CV",
            EN: "Upload your CV",
            ES: "Suba su CV",
            IT: "Carica il tuo CV",
            AL: "Laden Sie Ihren Lebenslauf hoch",
        },
        defaultPlaceholder: {
            FR: "Choisir un fichier",
            EN: "Choose a file",
            ES: "Elegir un archivo",
            IT: "Scegli un file",
            AL: "Datei auswählen",
        },
        defaultRequired: true,
        type: "file",
    },
];

export const RECRUITMENT_ADS_CONFIG: FormConfig = {
    basePath: ["SiteWeb", "Forms", "RecruitmentAds"],
    defaultInfoBlock: {
        BackgroundColor: "#4A5ACB",
        Image: {
            Desktop: { ...DEFAULT_EMPTY_MEDIA },
            Mobile: { ...DEFAULT_EMPTY_MEDIA },
        },
        Title: {
            FR: "Candidature spontanée",
            EN: "Spontaneous application",
            ES: "Candidatura espontánea",
            IT: "Candidatura spontanea",
            AL: "Initiativbewerbung",
        },
        TitleVisible: true,
        Description: {
            FR: "Envoyez-nous votre candidature !",
            EN: "Send us your application!",
            ES: "¡Envíenos su candidatura!",
            IT: "Inviateci la vostra candidatura!",
            AL: "Senden Sie uns Ihre Bewerbung!",
        },
        DescriptionVisible: true,
        Advantages: [
            {
                Text: {
                    FR: "Rejoignez une équipe dynamique",
                    EN: "Join a dynamic team",
                    ES: "Únase a un equipo dinámico",
                    IT: "Unisciti a un team dinamico",
                    AL: "Treten Sie einem dynamischen Team bei",
                },
                Activer: true,
            },
            {
                Text: {
                    FR: "Évoluez dans un environnement stimulant",
                    EN: "Grow in a stimulating environment",
                    ES: "Evolucione en un entorno estimulante",
                    IT: "Cresci in un ambiente stimolante",
                    AL: "Wachsen Sie in einem anregenden Umfeld",
                },
                Activer: true,
            },
            {
                Text: {
                    FR: "Formation continue assurée",
                    EN: "Ongoing training provided",
                    ES: "Formación continua garantizada",
                    IT: "Formazione continua garantita",
                    AL: "Kontinuierliche Weiterbildung gewährleistet",
                },
                Activer: true,
            },
        ],
    },
    defaultFormTitle: {
        FR: "Candidature spontanée",
        EN: "Spontaneous application",
        ES: "Candidatura espontánea",
        IT: "Candidatura spontanea",
        AL: "Initiativbewerbung",
    },
    defaultFormSubtitle: DEFAULT_FORM_SUBTITLE,
    defaultSubmitButton: {
        Label: {
            FR: "Soumettre ma candidature →",
            EN: "Submit my application →",
            ES: "Enviar mi candidatura →",
            IT: "Invia la mia candidatura →",
            AL: "Meine Bewerbung einreichen →",
        },
        Color: "#4A5ACB",
    },
    defaultLegalText: DEFAULT_LEGAL_TEXT,
    sectionDefs: [
        {
            key: "ApplicationInfo",
            titleKey: "Application information",
            defaultTitle: {
                FR: "INFORMATIONS DE CANDIDATURE",
                EN: "APPLICATION INFORMATION",
                ES: "INFORMACIÓN DE CANDIDATURA",
                IT: "INFORMAZIONI SULLA CANDIDATURA",
                AL: "BEWERBUNGSINFORMATIONEN",
            },
            fields: RECRUITMENT_FIELDS,
        },
    ],
};
